import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  Signal,
  signal,
  viewChild,
} from '@angular/core';

import {
  InfoBannerComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AppState } from '../../app.reducer';
import { SlotType } from '../../enums/slot-type.enum';
import { StripeProductType } from '../../enums/stripe.enum';
import { FontawesomeService } from '../../services/fontawesome.service';
import { loadSubscription } from '../../store/subscription/subscription.actions';
import {
  selectBasicSubscription,
  selectQuotaExpired,
  selectRemainingQuota,
  selectSubscribed,
  selectSubscription,
  selectSubscriptionLoaded,
  selectSubscriptionLoading,
  selectTotalAgentsQuota,
  selectTotalQuota,
  selectUsedQuota,
} from '../../store/subscription/subscription.reducer';
import { AgentsBookingModalComponent } from '../agents-booking-modal/agents-booking-modal.component';
import { BaseBookingModalComponent } from '../base-booking-modal/base-booking-modal.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-slot-selector',
  templateUrl: './slot-selector.component.html',
  styleUrls: ['./slot-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    InfoBannerComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    ButtonComponent,
    AgentsBookingModalComponent,
    BaseBookingModalComponent,
    NgxSkeletonLoaderModule,
  ],
})
export class SlotSelectorComponent {
  initialSlotType = input<SlotType | null>(null);
  isOpen = input<boolean>(false);

  selectedSlotType = input<SlotType | null>(null);
  selectedSlotTypeChange = output<SlotType | null>();

  private readonly store = inject(Store<AppState>);
  readonly fontawesomeService = inject(FontawesomeService);
  readonly themingService = inject(ThemingService);
  private readonly transloco = inject(TranslocoService);
  private readonly agentsBookingModal =
    viewChild<AgentsBookingModalComponent>('agentsBookingModal');
  private readonly baseBookingModal =
    viewChild<BaseBookingModalComponent>('baseBookingModal');

  SlotType = SlotType;

  subscribed: Signal<boolean> = this.store.selectSignal(selectSubscribed);
  totalAgentsQuota: Signal<number> = this.store.selectSignal(
    selectTotalAgentsQuota,
  );
  basicSubscription: Signal<boolean> = this.store.selectSignal(
    selectBasicSubscription,
  );
  subscription = this.store.selectSignal(selectSubscription);
  readonly loaded = this.store.selectSignal(selectSubscriptionLoaded);
  readonly loading = this.store.selectSignal(selectSubscriptionLoading);

  readonly showLoading = computed(() => !this.loaded() || !!this.loading());

  readonly bookingInProgress = signal(false);
  private readonly selectAgentAfterBooking = signal(false);

  readonly showAgentOption = computed(
    () => this.subscribed() && !this.basicSubscription(),
  );

  constructor() {
    effect(() => {
      if (!this.selectAgentAfterBooking()) return;
      if (!this.loaded()) return;
      if (!this.hasAgentSubscription() || this.agentDisabled()) return;

      this.selectAgentAfterBooking.set(false);
      this.selectedSlotTypeChange.emit(SlotType.ACTIVE_SOURCING);
    });

    effect(() => {
      if (this.showLoading()) return;
      const selected = this.selectedSlotType();
      if (!this.isSlotUnavailable(selected)) return;

      const next = this.firstAvailableSlot();
      if (next !== selected) {
        this.selectedSlotTypeChange.emit(next);
      }
    });
  }

  /**
   * Whether the given slot is currently exhausted and cannot be booked.
   * @param {SlotType | null} slotType Slot to check
   * @returns {boolean} True if the slot cannot be selected
   */
  private isSlotUnavailable(slotType: SlotType | null): boolean {
    if (slotType === SlotType.ACTIVE_SOURCING) return this.agentDisabled();
    if (slotType === SlotType.BASIC) return this.basicDisabled();
    return false;
  }

  readonly hasAgentSubscription = computed(
    () => !!this.subscription()?.subscriptionSummary?.agentsSubscription?.name,
  );

  readonly agentQuotaExpired = computed(() =>
    this.getQuotaExpired(SlotType.ACTIVE_SOURCING)(),
  );

  readonly agentDisabled = computed(
    () => this.showAgentOption() && this.agentQuotaExpired(),
  );

  readonly basicDisabled = computed(
    () => this.getQuotaExpired(SlotType.BASIC)() && this.subscribed(),
  );

  /**
   * Highest slot that can currently be booked.
   * @returns {SlotType} Agent, Basic, or Not listed
   */
  readonly firstAvailableSlot = computed(() => {
    if (this.showAgentOption() && !this.agentDisabled()) {
      return SlotType.ACTIVE_SOURCING;
    }
    if (!this.basicDisabled()) {
      return SlotType.BASIC;
    }
    return SlotType.NOT_LISTED;
  });

  readonly showBookAgentCta = computed(
    () => this.showAgentOption() && !this.hasAgentSubscription(),
  );

  readonly showUpgradeAgentCta = computed(
    () =>
      this.showAgentOption() &&
      this.hasAgentSubscription() &&
      this.agentQuotaExpired(),
  );

  readonly showUpgradeSubscriptionCta = computed(() => {
    if (!this.getQuotaExpired(SlotType.BASIC)()) {
      return false;
    }
    if (this.showAgentOption() && !this.agentQuotaExpired()) {
      return false;
    }
    const planActions =
      this.subscription()?.subscriptionSummary?.planActions?.[
        StripeProductType.BASIC
      ];
    return !!planActions?.canChange || !!planActions?.canBook;
  });

  /**
   * Whether the slot-limit banner should show: both Basic and Agent Sourcing
   * pools exist and are exhausted.
   * @returns {boolean}
   */
  readonly showLimitBanner = computed(() => {
    if (this.basicSubscription()) {
      return false;
    }
    const basicTotal = this.getTotalQuota(SlotType.BASIC)() ?? 0;
    const agentTotal = this.getTotalQuota(SlotType.ACTIVE_SOURCING)() ?? 0;
    if (basicTotal <= 0 || agentTotal <= 0) {
      return false;
    }
    return (
      this.getQuotaExpired(SlotType.BASIC)() &&
      this.getQuotaExpired(SlotType.ACTIVE_SOURCING)()
    );
  });

  readonly agentAvailableLabel = computed(() =>
    this.availableQuotaLabel(SlotType.ACTIVE_SOURCING),
  );

  readonly basicAvailableLabel = computed(() => {
    if (!this.subscribed() || this.basicSubscription()) {
      return null;
    }
    return this.availableQuotaLabel(SlotType.BASIC);
  });

  /**
   * Remaining-vs-total quota label for a slot type.
   *
   * @param {SlotType} type Slot type to format
   * @returns {string} Localized "X of Y available" label
   */
  private availableQuotaLabel(type: SlotType): string {
    const remaining = this.getRemainingQuota(type)() ?? 0;
    const total = this.getTotalQuota(type)() ?? 0;
    return this.transloco.translate(
      'company-tool.snippets.slot-selector.selection.available',
      { free: remaining, total },
    );
  }

  pick(type: unknown): void {
    const slotType = type as SlotType | null;
    if (slotType === SlotType.ACTIVE_SOURCING && this.agentDisabled()) {
      return;
    }
    if (slotType === SlotType.BASIC && this.basicDisabled()) {
      return;
    }
    this.selectedSlotTypeChange.emit(slotType);
  }

  onBookAgent(): void {
    // First book and plan upgrades both go through the agents modal
    // (checkout for new sub, changeSubscription when Agents sub already exists).
    this.agentsBookingModal()?.open();
  }

  /**
   * Opens the base subscription upgrade/booking modal.
   * @returns {void}
   */
  onUpgradeSubscription(): void {
    this.baseBookingModal()?.open();
  }

  /**
   * Reloads subscription after Agents booking and selects the Agent slot once available.
   * @returns {void}
   */
  onAgentBooked(): void {
    this.selectAgentAfterBooking.set(true);
    this.store.dispatch(loadSubscription());
  }

  /**
   * Reloads subscription after a base plan change so slot quotas refresh.
   * @returns {void}
   */
  onBasePlanChanged(): void {
    this.store.dispatch(loadSubscription());
  }

  getUsedQuota(type: SlotType) {
    return this.store.selectSignal(selectUsedQuota(type));
  }

  getTotalQuota(type: SlotType) {
    return this.store.selectSignal(selectTotalQuota(type));
  }

  getQuotaExpired(type: SlotType) {
    return this.store.selectSignal(
      selectQuotaExpired(type, this.isOpen() ? this.initialSlotType() : null),
    );
  }

  getRemainingQuota(type: SlotType) {
    return this.store.selectSignal(
      selectRemainingQuota(type, this.isOpen() ? this.initialSlotType() : null),
    );
  }
}
